import type {PaymentActionKind} from "../../types";

/** Предел размера чека на клиенте; тот же лимит проверяет бэкенд. */
export const MAX_RECEIPT_BYTES = 15 * 1024 * 1024;

/**
 * Типы файлов, принимаемые чеком: документ и три растровых формата, которыми
 * трейдеры снимают экран банковского приложения.
 *
 * Здесь набор один на трейдерскую и админскую формы — иначе они разъезжаются, и
 * файл, принятый одной, отвергается другой. Последнее слово всё равно за
 * бэкендом: он читает тип из самих байтов файла, а браузер сообщает то, что
 * прочитал из имени.
 */
export const RECEIPT_FILE_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "webp"] as const;

/**
 * Расширение файла по его mime — та же таблица, прочитанная в обратную сторону.
 * Нужна скачиванию: имя файла собирается на клиенте, и без неё скриншот
 * сохранялся бы под именем `.pdf`.
 */
export const RECEIPT_EXTENSION_BY_MIME: Readonly<Record<string, string>> = {
    "application/pdf": "pdf",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
};

export const RECEIPT_MIME_TYPES = Object.keys(RECEIPT_EXTENSION_BY_MIME);

/**
 * Меняет расширение имени на то, которое обещает mime ответа. Незнакомый mime
 * имя не трогает: гадать за сервер хуже, чем оставить как есть.
 */
export function withReceiptExtension(fileName: string, mimeType: string): string {
    const ext = RECEIPT_EXTENSION_BY_MIME[mimeType.split(";")[0].trim().toLowerCase()];
    if (!ext) return fileName;
    const base = fileName.replace(/\.[^./\\]+$/, "");
    return `${base}.${ext}`;
}

/** Значение accept у файлового инпута — и mime, и расширения. */
export const RECEIPT_FILE_ACCEPT =
    "application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp";

/** Подпись про типы и лимит, одна на обе формы. */
export const RECEIPT_FILE_HINT = "PDF или изображение (JPG/PNG/WEBP) · до 15 МБ";

/**
 * Тип подходит, если его подтверждает расширение ИЛИ mime от браузера.
 *
 * Именно «или», а не «и»: mime у выбранного файла бывает пустым (перетаскивание
 * из архиватора, редкие мобильные браузеры), и требование обоих признаков
 * отвергало бы нормальные файлы. Подделку это не пропускает — содержимое
 * проверяет бэкенд.
 */
export function isSupportedReceiptFile(file: File): boolean {
    const name = file.name.toLowerCase();
    const byExtension = RECEIPT_FILE_EXTENSIONS.some((ext) => name.endsWith(`.${ext}`));
    const byMime = RECEIPT_MIME_TYPES.includes(file.type);
    return byExtension || byMime;
}

export interface ReceiptDraft {
    file: File;
    fileName: string;
    fileSize: number;
    mimeType: string;
    objectUrl: string;
    /**
     * id, который вернула успешная загрузка этого файла. Пусто, пока файл не
     * загружен.
     *
     * Нужен повторной отправке: закрытие могло отказать уже ПОСЛЕ загрузки
     * (сумма больше остатка, 409 по В-22, обрыв на середине пачки), и вторая
     * попытка без этой памяти залила бы те же файлы заново — лишние строки в
     * `receipts` при одном переводе. Драфт с известным id при повторе не
     * грузится, а предъявляется.
     */
    receiptId?: string;
}

/**
 * Четыре финальных действия трейдера: закрыть заявку, вернуть её в пул, открыть
 * спор и отменить заявку насовсем (28.08.2026). Ввод реквизитов приёма —
 * не финальное.
 */
export type ConfirmAction = Exclude<PaymentActionKind, "setRequisites">;

/** Разбор суммы закрытия против остатка заявки. */
export interface AmountAnalysis {
    remaining: number;
    amount: number | null;
    valid: boolean;
    full: boolean;
    overpay: boolean;
    ok: boolean;
    currentPayment: number;
    maxPayments: number;
    hasLimit: boolean;
}

/** Разбор введённого курса (₽ за 1 USDT). */
export interface RateAnalysis {
    value: number | null;
    normalized: string | null;
    valid: boolean;
    precisionOk: boolean;
    ok: boolean;
}
